import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Crown, Download, Loader2, Printer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { fetchFunctionPdf, runPdf } from '@/lib/pdf';
import {
  SUPPORT_ADDRESS_LINE1,
  SUPPORT_ADDRESS_LINE2,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO_HREF,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TEL_HREF,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';
import type { GroupStatementData, GroupStatementStore } from '@/hooks/useGroupStatement';
import {
  AddressBlock,
  AgingBadge,
  AgingGrid,
  SummaryStat,
  daysPastDue,
  fmtDate,
  fmtLongDate,
  type BucketId,
} from './parts';

/**
 * The merged chain statement — one document covering every store in a chain,
 * grouped BY STORE with a subtotal each.
 *
 * This exists because a chain like The Melt bills as 26 separate portal
 * accounts but is paid by ONE accounting department. The per-store breakdown
 * is the whole point: a single combined number tells that department what to
 * pay but not which store to charge it to, which is the question they actually
 * have to answer internally.
 *
 * The ACCOUNT is the boundary: a store's section is everything open on that
 * store's QuickBooks account, so each subtotal reconciles against QuickBooks
 * and the chain total is the sum. The chain's separate equipment account is a
 * different billing relationship and is not on this document; an equipment
 * invoice billed to a STORE account is that store's balance and is.
 */
export default function ChainStatement({
  statement,
  footnote,
}: {
  statement: GroupStatementData;
  footnote?: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const today = useMemo(() => new Date(), []);
  const brand = statement.master.groupBrand ?? statement.master.name;

  // The server already computed aging for the PDF and the email; re-keying it
  // here rather than recomputing keeps the screen from ever disagreeing with
  // the document the customer downloads two seconds later.
  const aging: Record<BucketId, number> = {
    current: statement.aging.current,
    d30: statement.aging.d1_30,
    d60: statement.aging.d31_60,
    d90: statement.aging.d61_90,
    d90plus: statement.aging.d90plus,
  };
  const pastDue = statement.total_open - aging.current;

  const pdfName = `brix-statement-${brand.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${statement.as_of_date}.pdf`;

  // runPdf opens the print tab BEFORE the fetch — see openPdfTab.
  async function withPdf(mode: 'print' | 'download', setBusy: (b: boolean) => void) {
    setBusy(true);
    try {
      await runPdf(mode, () => fetchFunctionPdf('customer-group-statement-pdf'), pdfName);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ChainStatement] PDF failed:', err);
      if (mode === 'download') window.alert(`We couldn't download this statement. ${err instanceof Error ? err.message : ''}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-14 md:pb-0">
      <div data-print-doc className="px-6 md:px-8 pt-6 pb-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Link
            to="/stores"
            className="inline-flex items-center gap-1.5 text-sm text-slate-900 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <ArrowLeft size={14} /> Back to all stores
          </Link>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={downloading}
              onClick={() => void withPdf('download', setDownloading)}
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              Download PDF
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={printing}
              onClick={() => void withPdf('print', setPrinting)}
            >
              {printing ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
              Print
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep print:shadow-none print:border-0">
          <div className="h-3 bg-brix-primary-deep" aria-hidden />

          <div className="px-6 md:px-8 pt-7 pb-6 border-b border-brix-border flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <img src="/brand/brix-logo-full.png" alt="Brix Beverage" className="h-12 md:h-14 w-auto" />
                <span className="self-stretch w-px bg-brix-primary" aria-hidden />
                <img
                  src="/brand/alameda-soda-logo-navy.png"
                  alt="Alameda Soda"
                  className="h-7 md:h-8 w-auto"
                />
              </div>
              <p className="text-[10px] text-brix-muted">Alameda Point Beverage Group Inc.</p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl font-bold tracking-tight text-slate-900 leading-none">
                Account Statement
              </p>
              <p className="text-[11px] font-semibold text-brix-primary tracking-tight mt-1.5">
                All {brand} locations
              </p>
              <p className="text-xs font-semibold text-slate-900 tracking-tight mt-1">
                As of {fmtLongDate(today)}
              </p>
            </div>
          </div>

          <div className="px-6 md:px-8 py-5 grid grid-cols-1 md:grid-cols-2 gap-5 border-b border-brix-border">
            <AddressBlock
              label="From"
              name="Brix Beverage"
              lines={[
                'Hangar 25',
                SUPPORT_ADDRESS_LINE1,
                SUPPORT_ADDRESS_LINE2,
                `${SUPPORT_PHONE} · ${SUPPORT_PHONE_TOLL_FREE}`,
              ]}
            />
            <AddressBlock
              label="Account"
              name={statement.master.name}
              lines={[
                `${statement.stores.length} location${statement.stores.length === 1 ? '' : 's'} with an open balance`,
                statement.stores_with_nothing_open > 0
                  ? `${statement.stores_with_nothing_open} other location${statement.stores_with_nothing_open === 1 ? '' : 's'} fully paid`
                  : '',
              ]}
            />
          </div>

          <div className="px-6 md:px-8 py-5 border-b border-brix-border bg-brix-surface-alt/40">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
              <SummaryStat label="Total open balance" value={formatUsd(statement.total_open)} emphasis />
              <SummaryStat label="Open invoices" value={String(statement.open_count)} />
              <SummaryStat
                label="Past due"
                value={formatUsd(pastDue)}
                tone={pastDue > 0 ? 'warning' : undefined}
              />
            </div>
            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-2">Aging</div>
            <AgingGrid aging={aging} />
          </div>

          <div className="px-6 md:px-8 py-5 border-b border-brix-border">
            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-2">
              Open items by location
            </div>
            <div className="space-y-4">
              {statement.stores.map((store) => (
                <StoreSection key={store.customer_id} store={store} today={today} />
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border-2 border-brix-primary-deep bg-brix-surface-alt/60 px-4 py-3.5">
              <span className="text-sm font-bold tracking-tight text-slate-900">
                Total due — all {brand} locations
              </span>
              <span
                className="font-mono font-bold text-lg tracking-tight text-brix-primary"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(statement.total_open)}
              </span>
            </div>

            {footnote && (
              <p className="mt-3 text-[11px] leading-relaxed text-slate-500 tracking-tight">
                {footnote}
              </p>
            )}
          </div>

          <div className="px-6 md:px-8 py-5 bg-brix-surface-alt/60 border-t border-brix-border">
            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-1.5">
              Remit payment to
            </div>
            <p className="text-sm font-semibold text-slate-900 tracking-tight">Brix Beverage</p>
            <p className="text-[11px] text-slate-900 tracking-tight">
              Hangar 25 · {SUPPORT_ADDRESS_LINE1} · {SUPPORT_ADDRESS_LINE2}
            </p>
            <p className="text-[11px] text-slate-900 tracking-tight mt-1">
              <a href={SUPPORT_TEL_HREF} className="text-brix-primary">
                {SUPPORT_PHONE}
              </a>{' '}
              ·{' '}
              <a href={SUPPORT_TOLL_FREE_HREF} className="text-brix-primary">
                {SUPPORT_PHONE_TOLL_FREE}
              </a>{' '}
              ·{' '}
              <a href={SUPPORT_MAILTO_HREF} className="text-brix-primary">
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StoreSection({ store, today }: { store: GroupStatementStore; today: Date }) {
  return (
    <div className="rounded-xl border border-brix-border overflow-hidden">
      {/* Store band — the heading that makes the document answer "which store". */}
      <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-brix-primary-tint border-b border-brix-border">
        <span className="inline-flex items-center gap-2 min-w-0">
          {store.is_master ? (
            <Crown size={13} className="text-brix-primary shrink-0" />
          ) : (
            <Building2 size={13} className="text-brix-primary shrink-0" />
          )}
          <span className="text-sm font-bold tracking-tight text-slate-900 truncate">
            {store.store_code ? `${store.store_code} · ${store.name}` : store.name}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className="text-[10px] text-slate-500 mr-2">
            {store.invoices.length} open
          </span>
          <span
            className="font-mono font-bold text-sm tracking-tight text-slate-900"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatUsd(store.subtotal)}
          </span>
        </span>
      </div>

      <div className="hidden sm:grid grid-cols-[100px_1fr_100px_110px_110px] gap-3 px-3 py-2 text-[11px] font-semibold tracking-tight text-slate-500 bg-brix-surface-alt/50 border-b border-brix-border">
        <div>Invoice date</div>
        <div>Invoice #</div>
        <div>Due date</div>
        <div className="text-right">Original</div>
        <div className="text-right">Balance due</div>
      </div>

      {store.invoices.map((inv) => {
        const days = daysPastDue(inv.due_date, today);
        const overdue = days > 0;
        const to = `/invoices/${encodeURIComponent(inv.qbo_invoice_id)}`;
        return (
          <div key={inv.qbo_invoice_id}>
            {/* Mobile: stacked card */}
            <div className="sm:hidden px-3 py-3 border-b border-brix-border last:border-b-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={to}
                    className="text-sm font-bold font-mono tracking-tight text-brix-primary hover:underline"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    #{inv.doc_number}
                  </Link>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {fmtDate(inv.txn_date)} · Due {fmtDate(inv.due_date)}
                  </div>
                  <AgingBadge days={days} className="mt-1" />
                </div>
                <div
                  className={cn(
                    'text-right shrink-0 font-mono font-bold text-base tracking-tight',
                    overdue ? 'text-red-600' : 'text-slate-900',
                  )}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatUsd(inv.balance)}
                </div>
              </div>
            </div>

            {/* Desktop: aligned row */}
            <div className="hidden sm:grid grid-cols-[100px_1fr_100px_110px_110px] gap-3 px-3 py-2.5 items-center text-sm border-b border-brix-border last:border-b-0">
              <div className="text-slate-900 font-medium">{fmtDate(inv.txn_date)}</div>
              <div className="min-w-0">
                <Link
                  to={to}
                  className="font-mono font-bold tracking-tight text-brix-primary hover:underline"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  #{inv.doc_number}
                </Link>
                <AgingBadge days={days} className="ml-2 align-middle" />
              </div>
              <div className="text-slate-900 font-medium">{fmtDate(inv.due_date)}</div>
              <div
                className="text-right font-mono text-slate-900 tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(inv.original)}
              </div>
              <div
                className={cn(
                  'text-right font-mono font-bold tracking-tight',
                  overdue ? 'text-red-600' : 'text-slate-900',
                )}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(inv.balance)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
